/**
 * The notification outbox: what is waiting, what ended without sending, and
 * whether anything is late.
 *
 * Counts only, never a payload — the outbox payload carries the rendered email
 * values, and this page shows counts.
 *
 * THE TWO LISTS ARE GROUPED BY DIFFERENT THINGS, deliberately. What is WAITING
 * is grouped by `reason`, the thing holding it. What ENDED is grouped by
 * `state` and `outcome`, the thing that ended it, with `reason` as a second
 * column. Both lists used to group on `reason`, so a line reading "Quiet hours"
 * told an operator a message had once been held overnight and nothing at all
 * about why it died.
 *
 * The ended list reads BOTH terminal states (issue #3076). It used to read
 * `exhausted` alone, and every seam refusal — `opted_out`, `no_address`,
 * `stage_paused`, `holdout`, `superseded`, `stage_resolved` — is a CANCEL, so
 * the list could never show the rows it existed to tell apart. A row the rail
 * GAVE UP on is marked on its line; the rest are retirements with a stated
 * reason, most of them correct.
 *
 * The ended list is bounded to the last 24 hours by the endpoint, on purpose:
 * the question an operator asks of a terminal state is "is this happening NOW".
 *
 * THE AMBER STATE READS ONE NUMBER — how far the most overdue DUE row is past
 * its send time. Never the oldest age: every waiting row waits on purpose, so
 * that age is large on a healthy rail (see `STALE_OVERDUE_SECONDS` in
 * `../data`, which also derives the threshold).
 *
 * Every sentence on this card is a constant in `../data`, so there is one copy
 * of each and the test can pin it.
 */
import {
  endedRows,
  endedRowView,
  endedTotalLine,
  formatAge,
  formatCount,
  humanLabel,
  outboxOverdueView,
  tileBody,
  OUTBOX_COPY,
  type ExhaustedBucket,
  type OutboxData,
  type Tile,
} from '../data';
import { TileUnavailable, tileShell } from '../../system-health/components/TileStatus';

function Buckets({ rows, empty }: { rows: OutboxData['pending']; empty: string }) {
  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">{empty}</p>;
  }
  return (
    <ul className="space-y-1 text-sm tabular-nums">
      {rows.map((b) => (
        <li key={`${b.rail}:${b.reason}`} className="flex justify-between gap-4">
          <span className="text-muted-foreground" title={`${b.rail} / ${b.reason}`}>
            {humanLabel(b.rail)} · {humanLabel(b.reason)}
          </span>
          <span className="font-medium">{formatCount(b.count)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The "Ended without sending" list. The leading label is the TERMINAL cause
 * (`outcome`) with what it means; the reason the send was deferred is a
 * second, quieter line.
 *
 * This is the whole point of the panel: `no_address` is a data gap to fix,
 * `opted_out` is the member's own choice and correct, `attempts` is a rail
 * that gave up. Every line's words come from `endedRowView`, so the card holds
 * markup and nothing else.
 */
function EndedBuckets({ rows, empty }: { rows: ExhaustedBucket[] | null; empty: string }) {
  if (rows === null) {
    return <p className="text-xs text-muted-foreground">{OUTBOX_COPY.notReported}</p>;
  }
  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">{empty}</p>;
  }
  return (
    <ul className="space-y-2 text-sm tabular-nums">
      {rows.map(endedRowView).map((v) => (
        <li
          key={v.key}
          className={`flex items-baseline justify-between gap-4 ${
            v.gaveUp ? 'rounded border-l-2 border-amber-500 bg-amber-500/5 pl-2' : ''
          }`}
        >
          <span className={v.gaveUp ? 'text-amber-800' : 'text-muted-foreground'} title={v.title}>
            {v.gaveUp && (
              <span className="mr-2 rounded bg-amber-500/15 px-1 text-[10px] font-semibold uppercase tracking-wide">
                {OUTBOX_COPY.gaveUpTag}
              </span>
            )}
            {v.railLabel} · {v.outcomeLabel}
            {v.meaning !== null && <span className="block text-xs opacity-80">{v.meaning}</span>}
            <span className="block text-xs opacity-70">held: {v.heldLabel}</span>
          </span>
          <span className="font-medium">{formatCount(v.count)}</span>
        </li>
      ))}
    </ul>
  );
}

export function OutboxCard({ tile, loading }: { tile: Tile<OutboxData>; loading: boolean }) {
  const unavailable = tile.status === 'unavailable';
  const body = tileBody(tile, loading);
  const data = tile.data;
  const overdue = outboxOverdueView(data);
  const stale = !unavailable && overdue.kind === 'stale';

  return (
    <section
      className={`border rounded-lg p-6 ${tileShell(
        unavailable,
        stale ? 'border-amber-500/50 bg-amber-500/5' : 'bg-card',
      )}`}
    >
      <h3 className="text-lg font-semibold mb-1">Outbox</h3>
      <p className="text-xs text-muted-foreground mb-4">{OUTBOX_COPY.subtitle}</p>

      {unavailable && <TileUnavailable error={tile.error} />}

      {body === 'loading' && (
        <p className="text-sm text-muted-foreground">Loading the outbox…</p>
      )}

      {body === 'ready' && data && (
        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              {OUTBOX_COPY.waitingHeading}
            </div>
            <Buckets rows={data.pending} empty={OUTBOX_COPY.nothingWaiting} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              {OUTBOX_COPY.endedHeading}
            </div>
            <EndedBuckets rows={endedRows(data)} empty={OUTBOX_COPY.nothingEnded} />
            <p className="mt-2 text-xs text-muted-foreground">{endedTotalLine(data)}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              {OUTBOX_COPY.overdueHeading}
            </div>
            {overdue.seconds !== null && (
              <div className={`text-2xl font-semibold ${stale ? 'text-amber-700' : ''}`}>
                {formatAge(overdue.seconds)}
              </div>
            )}
            <p className={`mt-1 text-xs ${stale ? 'text-amber-700' : 'text-muted-foreground'}`}>
              {overdue.sentence}
            </p>

            <div className="mt-4 text-xs uppercase tracking-wide text-muted-foreground">
              {OUTBOX_COPY.oldestWaitingLabel}
            </div>
            <div className="text-sm font-medium tabular-nums">
              {formatAge(data.oldest_pending_age_seconds)}
            </div>
            <p className="text-[11px] text-muted-foreground">{OUTBOX_COPY.oldestWaitingNote}</p>
          </div>
        </div>
      )}
    </section>
  );
}
